//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import DateTimePicker from 'common/components/core/form/DateTimePicker';
import classnames from 'classnames';
import { Button, ButtonTypes } from 'common/components/core/button';
import moment from 'moment-timezone';
import theme from './SingleSlotSelectorWithOffset.scss';
import Select from 'common/components/core/select/Select';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';

const DefaultActions = ({ onDelete, theme, disabled }) => {
    return onDelete ? (
        <Button
            buttonType={ButtonTypes.Icon}
            circle
            icon="delete-bin"
            className={theme.deleteBtn}
            theme={theme}
            onClick={onDelete}
            disabled={disabled}
        />
    ) : null;
};

const daysList = Object.values(DayOfWeekType);

type Props = {
    start: moment,
    end: moment,
    slotFor: string,
    index?: number,
    theme: Object,
    error?: ?string,
    disabled?: boolean,
    onChange?: ({
        slotFor: string,
        index: number,
        start: moment,
        end: moment
    }) => void,
    onDelete?: ({ slotFor: string, index: number }) => void,
    endDayOffset: number
};
const singleSlotSelectorFactoryWithOffset = ({ Actions = DefaultActions }) => {
    class SingleSlotSelector extends React.PureComponent<Props> {
        static defaultProps = {
            endDayOffset: 0
        };

        handleSlotStartChange = (value: moment) => {
            const { end, endDayOffset } = this.props;
            this.handleSlotChange({ start: value, end, endDayOffset });
        };

        handleSlotEndChange = (value: moment) => {
            const { start, endDayOffset } = this.props;
            this.handleSlotChange({ start, end: value, endDayOffset });
        };

        handleSlotChange = ({
            start,
            end,
            endDayOffset = 0
        }: {
            start: moment,
            end: moment,
            endDayOffset: number
        }) => {
            const { slotFor, index, onChange } = this.props;
            onChange?.({
                slotFor,
                index,
                start,
                end,
                endDayOffset
            });
        };

        onDelete = () => {
            const { slotFor, index, onDelete } = this.props;
            onDelete?.({ slotFor, index });
        };

        getDayOptions = () => {
            const { slotFor, t } = this.props;
            const startIndex = daysList.indexOf(slotFor);
            const daysToShow = [
                ...daysList.slice(startIndex),
                ...daysList.slice(0, startIndex)
            ];
            return daysToShow.map(day => ({
                value: day,
                label: t(`shiftTable.days.${day}`)
            }));
        };

        changeEndOffset = ({ value }) => {
            const { start, end, slotFor } = this.props;
            const endDayOffset =
                (7 + daysList.indexOf(value) - daysList.indexOf(slotFor)) % 7;
            this.handleSlotChange({ start, end, endDayOffset });
        };

        render() {
            const {
                start,
                end,
                theme,
                error,
                disabled,
                t,
                index,
                slotFor,
                endDayOffset
            } = this.props;
            const endOffset =
                daysList[(daysList.indexOf(slotFor) + endDayOffset + 7) % 7];
            const min = endDayOffset === 0 ? start : null;
            return (
                <div
                    className={classnames(theme.horizontal, theme.slotSelector)}
                >
                    <DateTimePicker
                        testId={`start-${index}`}
                        autoFocus
                        theme={theme}
                        value={start}
                        shouldTriggerOnChangeForDateTimeOutsideRange
                        onChange={this.handleSlotStartChange}
                        inputClassName={theme.inputElement}
                        timeStep={15}
                        showDate={false}
                        error={error}
                        disabled={disabled}
                    />
                    <span className={theme.toText}>{t('slotSelector.to')}</span>
                    <Select
                        options={this.getDayOptions()}
                        value={endOffset}
                        isClearable={false}
                        theme={theme}
                        onChange={this.changeEndOffset}
                        disabled={disabled}
                    />
                    <DateTimePicker
                        testId={`end-${index}`}
                        autoFocus
                        theme={theme}
                        value={end}
                        min={min}
                        shouldTriggerOnChangeForDateTimeOutsideRange
                        onChange={this.handleSlotEndChange}
                        timeStep={15}
                        showDate={false}
                        error={error}
                        disabled={disabled}
                    />
                    <Actions
                        {...this.props}
                        onDelete={this.onDelete}
                        endDayOffset={endDayOffset}
                    />
                </div>
            );
        }
    }
    return themr('SingleSlotSelector', theme)(SingleSlotSelector);
};

const SingleSlotSelectorWithOffset = singleSlotSelectorFactoryWithOffset({});
export default SingleSlotSelectorWithOffset;
export { SingleSlotSelectorWithOffset, singleSlotSelectorFactoryWithOffset };
